/**
 * Instantes absolutos (ADR-006).
 *
 * Un `InstanteUtc` son milisegundos desde la epoca. Es un punto en la linea del tiempo,
 * sin zona y sin calendario: dos personas en dos paises distintos que miran el mismo
 * instante ven horas de reloj distintas, pero el instante es el mismo.
 *
 * Todo lo que se persiste es un instante (ADR-006). Las horas de reloj se derivan al
 * mostrarlas o al decidir la tarifa, siempre en `America/Montevideo`.
 *
 * **Nunca se parsea una fecha local con `new Date(texto)`.** La unica lectura de texto
 * que existe aca exige un designador de zona explicito (`Z` o `±HH:MM`), y ni siquiera
 * usa `Date.parse`: descompone el texto con una expresion regular y arma el instante con
 * `Date.UTC`, que recibe numeros y no admite interpretacion.
 */
import { errorDominio } from '../tipos/errores.js';
import { exito, fallo } from '../tipos/resultado.js';
/** Cota de seguridad: el rango que `Date` representa sin perder precision. */
const MAXIMO_EPOCA_MS = 8.64e15;
/** ISO-8601 con designador de zona OBLIGATORIO. Sin `Z` ni desfase, no se acepta. */
const PATRON_ISO = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?(Z|[+-]\d{2}:\d{2})$/;
/** Construye un instante a partir de milisegundos de epoca. */
export function instanteDesdeEpochMs(ms, campo) {
    if (!Number.isInteger(ms) || Math.abs(ms) > MAXIMO_EPOCA_MS) {
        return fallo(errorDominio('INSTANTE_INVALIDO', campo));
    }
    return exito(ms);
}
/**
 * Lee un instante de un texto ISO-8601 **con zona explicita**.
 *
 * Acepta `2026-08-01T23:30:00Z` y `2026-08-01T20:30:00-03:00`.
 * Rechaza `2026-08-01T23:30` y `2026-08-01`, que son horas locales sin zona: no
 * designan ningun instante, y adivinarlo es exactamente el error que este modulo evita.
 */
export function instanteDesdeIso(texto, campo) {
    const partes = PATRON_ISO.exec(texto);
    if (partes === null) {
        return fallo(errorDominio('INSTANTE_INVALIDO', campo));
    }
    const [, anioTexto, mesTexto, diaTexto, horaTexto, minutoTexto, segundoTexto, msTexto, zonaTexto,] = partes;
    // El patron garantiza que estos grupos existen; los opcionales se completan.
    if (anioTexto === undefined ||
        mesTexto === undefined ||
        diaTexto === undefined ||
        horaTexto === undefined ||
        minutoTexto === undefined ||
        zonaTexto === undefined) {
        return fallo(errorDominio('INSTANTE_INVALIDO', campo));
    }
    const anio = Number(anioTexto);
    const mes = Number(mesTexto);
    const dia = Number(diaTexto);
    const hora = Number(horaTexto);
    const minuto = Number(minutoTexto);
    const segundo = segundoTexto === undefined ? 0 : Number(segundoTexto);
    const milisegundo = msTexto === undefined ? 0 : Number(msTexto.padEnd(3, '0'));
    if (mes < 1 || mes > 12 || dia < 1 || dia > 31 || hora > 23 || minuto > 59 || segundo > 59) {
        return fallo(errorDominio('INSTANTE_INVALIDO', campo));
    }
    // Date.UTC recibe numeros, no texto: no hay nada que interpretar.
    const base = Date.UTC(anio, mes - 1, dia, hora, minuto, segundo, milisegundo);
    // Date.UTC normaliza en silencio (31 de abril pasa a 1 de mayo). Verificamos.
    const control = new Date(base);
    if (control.getUTCFullYear() !== anio ||
        control.getUTCMonth() !== mes - 1 ||
        control.getUTCDate() !== dia) {
        return fallo(errorDominio('INSTANTE_INVALIDO', campo));
    }
    let desfaseMs = 0;
    if (zonaTexto !== 'Z') {
        const signo = zonaTexto.startsWith('-') ? -1 : 1;
        const horasDesfase = Number(zonaTexto.slice(1, 3));
        const minutosDesfase = Number(zonaTexto.slice(4, 6));
        if (horasDesfase > 23 || minutosDesfase > 59) {
            return fallo(errorDominio('INSTANTE_INVALIDO', campo));
        }
        desfaseMs = signo * (horasDesfase * 60 + minutosDesfase) * 60_000;
    }
    return instanteDesdeEpochMs(base - desfaseMs, campo);
}
/** Escribe el instante como ISO-8601 en UTC, siempre con `Z`. */
export function isoDeInstante(instante) {
    return new Date(instante).toISOString();
}
/** Milisegundos de epoca. Para persistir y para pruebas. */
export function epochMsDe(instante) {
    return instante;
}
function asegurarInstante(ms) {
    if (!Number.isInteger(ms) || Math.abs(ms) > MAXIMO_EPOCA_MS) {
        // Violacion de invariante (ADR-034): los operandos ya eran instantes validos.
        throw new RangeError('INSTANTE_INVALIDO: el resultado se sale del rango representable.');
    }
    return ms;
}
/** Suma milisegundos. */
export function sumarMs(instante, ms) {
    return asegurarInstante(instante + ms);
}
/** Suma minutos. */
export function sumarMinutos(instante, minutos) {
    return asegurarInstante(instante + minutos * 60_000);
}
/** Suma horas. */
export function sumarHoras(instante, horas) {
    return asegurarInstante(instante + horas * 3_600_000);
}
/** Milisegundos exactos entre dos instantes: `hasta - desde`. */
export function diferenciaMs(desde, hasta) {
    return hasta - desde;
}
/**
 * Minutos **exactos** entre dos instantes.
 *
 * Devuelve un fallo si la diferencia no es un numero entero de minutos, en vez de
 * redondear en silencio. Redondear es lo que convierte "faltan 47 h 59 min 59,999 s"
 * en una comparación temporal de negocio y cambia el resultado en el límite.
 */
export function diferenciaMinutosExacta(desde, hasta) {
    const ms = hasta - desde;
    if (ms % 60_000 !== 0) {
        return fallo(errorDominio('INSTANTE_INVALIDO'));
    }
    return exito(ms / 60_000);
}
/** Compara dos instantes: -1, 0 o 1. */
export function compararInstantes(a, b) {
    if (a < b)
        return -1;
    if (a > b)
        return 1;
    return 0;
}
/** `a` es anterior a `b`. */
export function esAnterior(a, b) {
    return a < b;
}
/** `a` es posterior a `b`. */
export function esPosterior(a, b) {
    return a > b;
}
//# sourceMappingURL=instante.js.map